import { BoltIcon } from '@heroicons/react/24/solid';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import type { ActivityDefinition, ActivityLog } from '../../../types/activityTracking';
import { frequencyLabel } from '../../../content/activityTemplates';

const DAY_ABBREVS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

interface ActivityGridProps {
  definitions: ActivityDefinition[];
  log: ActivityLog;
  weekDates: string[];
  onToggle?: (activityId: string, dateStr: string, completed: boolean) => void;
  compact?: boolean;
  disabled?: boolean;
  /** If provided, use these definitions (instead of `definitions`) for daily total counts */
  totalsSource?: ActivityDefinition[];
}

export function ActivityGrid({
  definitions,
  log,
  weekDates,
  onToggle,
  compact = false,
  disabled = false,
  totalsSource,
}: ActivityGridProps) {
  const active = definitions.filter(d => d.isActive);
  const totalsActive = (totalsSource ?? definitions).filter(d => d.isActive);
  const totalsActiveIds = new Set(totalsActive.map(d => d.id));

  if (active.length === 0) {
    return (
      <p className="text-sm text-surface-500 dark:text-surface-400 text-center py-4">
        No activities added yet.
      </p>
    );
  }

  function isCompleted(activityId: string, dateStr: string): boolean {
    return (log.completions[activityId] ?? []).includes(dateStr);
  }

  function completionCount(activityId: string): number {
    return (log.completions[activityId] ?? []).length;
  }

  function goalMet(def: ActivityDefinition): boolean {
    return completionCount(def.id) >= def.targetFrequency;
  }

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[340px]">
        <thead>
          <tr>
            <th className="text-left pb-2 pr-2" style={{ width: compact ? '40%' : '45%' }}>
              <span className="text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wide">
                Activity
              </span>
            </th>
            {weekDates.map((dateStr) => {
              const dayIndex = new Date(dateStr + 'T12:00:00').getDay();
              const isToday = dateStr === today;
              return (
                <th key={dateStr} className={`pb-2 text-center ${compact ? 'px-0.5' : 'px-1'}`}>
                  <span className={`text-xs font-medium ${
                    isToday
                      ? 'text-teal-600 dark:text-teal-400 font-bold'
                      : 'text-surface-400 dark:text-surface-500'
                  }`}>
                    {DAY_ABBREVS[dayIndex]}
                  </span>
                </th>
              );
            })}
            {!compact && (
              <th className="pb-2 pl-2 text-right">
                <span className="text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wide">
                  Goal
                </span>
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-surface-100 dark:divide-surface-800 border-b border-surface-100 dark:border-surface-800">
          {active.map((def) => (
            <tr key={def.id}>
              <td className="py-2 pr-2">
                <div className="flex items-center gap-1.5">
                  {def.isAutoTracked && (
                    <BoltIcon className="w-3.5 h-3.5 text-navy-400 dark:text-navy-300 flex-shrink-0" title="Auto-tracked" />
                  )}
                  <span className={`${compact ? 'text-xs' : 'text-sm'} text-surface-700 dark:text-surface-300 truncate`}>
                    {def.name}
                  </span>
                </div>
                {!compact && (
                  <span className="text-xs text-surface-400 dark:text-surface-500">
                    {frequencyLabel(def.targetFrequency)}
                  </span>
                )}
              </td>
              {weekDates.map((dateStr) => {
                const done = isCompleted(def.id, dateStr);
                const isAuto = def.isAutoTracked;
                const isFuture = dateStr > today;
                return (
                  <td key={dateStr} className={`py-2 ${compact ? 'px-0.5' : 'px-1'} text-center`}>
                    <button
                      type="button"
                      disabled={isAuto || disabled || isFuture}
                      onClick={() => !isAuto && !isFuture && onToggle?.(def.id, dateStr, !done)}
                      className={`
                        ${compact ? 'w-6 h-6' : 'w-8 h-8'}
                        rounded-full flex items-center justify-center mx-auto
                        transition-all duration-150
                        ${isAuto
                          ? done
                            ? 'bg-navy-700 dark:bg-navy-600 cursor-default'
                            : 'border-2 border-navy-300 dark:border-navy-600 cursor-default'
                          : isFuture
                            ? 'border-2 border-surface-200 dark:border-surface-700 opacity-30 cursor-default'
                            : done
                              ? 'bg-teal-500 dark:bg-teal-600 hover:bg-teal-600 dark:hover:bg-teal-500'
                              : 'border-2 border-surface-300 dark:border-surface-600 hover:border-teal-400 dark:hover:border-teal-500'
                        }
                      `}
                      aria-label={`${def.name} on ${dateStr}: ${done ? 'done' : 'not done'}`}
                    >
                      {done && (
                        <CheckCircleIcon className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-white`} />
                      )}
                    </button>
                  </td>
                );
              })}
              {!compact && (
                <td className="py-2 pl-2 text-right">
                  <span className={`text-xs font-medium ${
                    goalMet(def)
                      ? 'text-success-600 dark:text-success-400'
                      : 'text-surface-400 dark:text-surface-500'
                  }`}>
                    {completionCount(def.id)}/{def.targetFrequency}
                    {goalMet(def) && ' ✓'}
                  </span>
                </td>
              )}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td className="py-2 pr-2">
              <span className={`${compact ? 'text-xs' : 'text-sm'} font-semibold text-surface-500 dark:text-surface-400`}>
                Daily Total
              </span>
            </td>
            {weekDates.map((dateStr) => {
              const count = Object.entries(log.completions)
                .filter(([id, dates]) => totalsActiveIds.has(id) && dates.includes(dateStr))
                .length;
              const isToday = dateStr === today;
              return (
                <td key={dateStr} className={`py-2 ${compact ? 'px-0.5' : 'px-1'} text-center`}>
                  <span className={`${compact ? 'text-xs' : 'text-sm'} font-semibold ${
                    isToday
                      ? 'text-teal-600 dark:text-teal-400'
                      : 'text-surface-500 dark:text-surface-400'
                  }`}>
                    {count > 0 ? count : '—'}
                  </span>
                </td>
              );
            })}
            {!compact && <td />}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
